<script setup>
  import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { Loader2, Plus, RotateCcw } from "lucide-vue-next";
  import { IndexPageLayout } from "@/components/App/IndexPage";
  import { Button } from "@/components/ui/button";
  import { Sheet, SheetContent } from "@/components/ui/sheet";
  import ProfileAiBanner from "@/components/Survey/Profile/ProfileAiBanner.vue";
  import ProfileEdit from "@/components/Survey/Profile/ProfileEdit.vue";
  import ProfileTable from "@/components/Survey/Profile/ProfileTable.vue";
  import ProgrammingCheckReport from "@/components/Survey/ProgrammingCheck/ProgrammingCheckReport.vue";
  import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";
  import { useProfileListStore } from "@stores/survey/profiles/profileList";
  import { useAssistantStore } from "@stores/assistant";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";

  const props = defineProps({
    survey: {
      type: Object,
      required: true,
    },
  });

  const router = useRouter();
  const isDraftOrReview = computed(() => ["draft", "review"].includes(props.survey?.status));
  const instance = getCurrentInstance();
  const cable = instance.proxy.$cable;

  const surveyProfilesStore = useSurveyProfilesStore();
  const { surveyProfiles, selectedProfile, locked, noProfileExist, programmingCheck, checking } =
    storeToRefs(surveyProfilesStore);
  const { selectProfile, addProfile } = useProfileListStore();
  const assistant = useAssistantStore();
  const { selectCurrentCard } = useSurveyCardsStore();

  const editing = ref(false);
  const check = computed(() =>
    programmingCheck.value?.surveyToken === props.survey.token ? programmingCheck.value : null,
  );
  const loaded = computed(() => Boolean(check.value));

  const hasReport = computed(() => check.value?.status === "done" && !checking.value);
  const outcome = computed(() =>
    check.value?.report?.profiles?.find((row) => row.token === selectedProfile.value?.token),
  );

  const create = () => {
    addProfile();
    edit(surveyProfiles.value.length - 1);
  };

  const edit = (index) => {
    selectProfile(index);
    editing.value = true;
  };

  const closeSheet = (open) => {
    editing.value = open;
    if (!open) surveyProfilesStore.discardChanges();
  };

  const openQuestions = () =>
    router.push({ name: "SurveyQuestions", params: { token: props.survey.token }, hash: "#edit" });

  const openCard = async (token) => {
    await openQuestions();
    await nextTick();
    selectCurrentCard({ token });
  };

  const fix = async (prompt) => {
    await openQuestions();
    await nextTick();
    assistant.openWith(prompt);
  };

  onUnmounted(() => {
    surveyProfilesStore.stopRefresh();
    cable._removeChannel("ProfilesStatusChannel", instance.uid);
  });

  onMounted(() => {
    cable.subscribe({ channel: "ProfilesStatusChannel", survey_token: props.survey.token });
    cable._addChannel("ProfilesStatusChannel", surveyProfilesStore.profilesChannelHandlers(cable, props.survey.token), {
      _uid: instance.uid,
      _value: instance.proxy,
    });
  });
</script>

<template>
  <div
    data-cy="survey-profiles-page"
    class="h-[calc(100vh-100px)]">
    <div
      v-if="!isDraftOrReview"
      class="flex justify-center">
      <span>This page is only available for draft or review surveys</span>
    </div>
    <IndexPageLayout
      v-else
      title="Fake respondents"
      padding="none"
      fill>
      <template #actions>
        <Button
          variant="outline"
          :disabled="locked"
          @click="create">
          <Plus class="size-4" />
          Add a profile
        </Button>
        <Button
          :disabled="locked || noProfileExist"
          @click="surveyProfilesStore.runAll(survey.token)">
          <RotateCcw class="size-4" />
          Run all profiles
        </Button>
      </template>
      <template #banner>
        <div class="flex flex-col gap-3">
          <span class="text-muted-foreground">
            Simulated respondents walk the questionnaire. Their answers are flagged and never mixed with real data.
          </span>
          <ProfileAiBanner :survey-token="survey.token" />
        </div>
      </template>
      <div
        v-if="!loaded"
        class="flex h-full items-center justify-center">
        <Loader2 class="size-6 animate-spin text-muted-foreground" />
      </div>
      <div
        v-show="loaded"
        class="flex h-full">
        <div class="min-w-0 flex-1 overflow-y-auto">
          <ProfileTable
            :report="check?.report"
            @edit="edit" />
        </div>
        <div
          v-show="hasReport"
          class="w-1/3 shrink-0 overflow-y-auto border-l">
          <ProgrammingCheckReport
            :survey-token="survey.token"
            :disabled="locked"
            @rerun="surveyProfilesStore.checkProgramming(survey.token)"
            @card="openCard"
            @fix="fix" />
        </div>
      </div>
    </IndexPageLayout>

    <Sheet
      :open="editing"
      @update:open="closeSheet">
      <SheetContent class="w-[560px] gap-0 sm:max-w-[560px]">
        <ProfileEdit
          :outcome="outcome"
          @close="editing = false" />
      </SheetContent>
    </Sheet>
  </div>
</template>
